// tslint:disable-next-line:no-submodule-imports
import { all, call, put, putResolve, select } from 'redux-saga/effects';
import { About, Application, ApplicationTokenResponse, CheckSelfInfo, AASTheme, User, UserProfile } from '../../clients/aasServerClient.generated';
import { getAllApplications } from '../../repositories/ApplicationRepository';
import * as authRepository from '../../repositories/AuthenticationRepository';
import { getAllGroups } from '../../repositories/GroupRepository';
import { getAllServers } from '../../repositories/ServerRepository';
import { getAllUsers } from '../../repositories/UserRepository';
import { getAllWorkspaces } from '../../repositories/WorkspaceRepository';
import { fetchAllApplications, storeAllApplications, fetchTableApplications } from '../applications/actionCreators';
import { fetchAllGroups, storeAllGroups, fetchTableGroups } from '../groups/actionCreators';
import { takeEveryWithErrorHandling } from '../sagas';
import { storeAllServers, fetchTableServers } from '../servers/actionCreators';
import { fetchAllUsers, storeAllUsers, fetchTableUsers } from '../users/actionCreators';
import { fetchAllWorkspaces, storeAllWorkspaces, fetchTableWorkspaces } from '../workspaces/actionCreators';
import {
    authenticationStateChanged, checkLogin as checkLoginAction, dialogSuccess,
    fetchAllData as fetchAllDataAction, getLoginInfo as getLoginInfoAction,
    getSelfInfo as getSelfInfoAction, getTheme as getThemeAction,
    goToApplication as goToApplicationAction, setAbout, setInvalidToken, setSelfInfo,
    setSessions, setTheme
} from './actionCreators';
import {
    ActionTypeNames, AnonymousLoginAction, CheckResetPasswordTokenAction, GoToApplicationAction,
    LoginAction, LoginAsAction, LogoutAction, LogoutAsAction,
    ResetPasswordAction, SendPasswordResetAction, UpdateAccountAction, ConfirmActiveDirectoryAction, VerifyEmailAction
} from './actions';
import { ssoEnabled, isLoggedInAs, adEnabled } from './selectors';
import { activePage } from '../navigation/selectors';
import queryString from 'query-string';
import { getApplications } from '../../repositories/ApplicationRepository';
import { goToPage } from '../tables/actionCreators';
import Cookies from 'js-cookie';

function* watchCheckLogin() {
    yield takeEveryWithErrorHandling(ActionTypeNames.CheckLogin, checkLogin);
}

export function* checkLogin() {
// get the task and callback url from the query
const values = queryString.parse(window.location.search);
let _task: string = "";
let _url: string = "";
let _email: string = "";
let _username: string = "";
let application: string = '';
if (typeof values.task === 'string' ) {
    _task = values.task;
    _url = typeof values.url === 'string' ? decodeURIComponent(escape(values.url)) : '';
    if (_url !== '' && _url.split('?').length > 1) {
        const subValues = queryString.parse(_url.split('?')[1]);
        _email = typeof subValues.email === 'string' ? subValues.email : '';
        _username = typeof subValues.userName === 'string' ? subValues.userName : '';
    }
    }
    if (typeof values.application === 'string') {
        application = values.application;
    }
    // If ad is enabled and there is a token in the string we assume
    // we try to do an anonymous login
    if (yield select(adEnabled) && typeof values.token === 'string') {
        yield putResolve(
            authenticationStateChanged({
              alias: undefined,
              authenticated: false,
              privileges: undefined,
              sso: false,
              user: undefined,
            })
        );
        return;
    }
    yield putResolve(getThemeAction());
    const loggedIn = yield call(authRepository.checkLogin, _task, _url, _email, _username);
    if (!loggedIn.value && loggedIn.ssoHost && window.location.host !== loggedIn.ssoHost) {
        window.location.host = loggedIn.ssoHost
    } else if (application !== '' && loggedIn.value) {
        let query = '';
        Object.keys(values).forEach(k => {
          if (k === 'application') return;
          query += '&' + k + '=' + values[k];
        });
      authRepository.getSelfInfo().then(selfInfo => {
        const app: Application | undefined = selfInfo.applications.find(
          app => app.location_path.toLowerCase() == application.toLowerCase()
        );
        if (app) {
          authRepository.getApplicationToken(app.id).then(res => {
            window.location.href = `${res.anagoUrl}?accessToken=${res.token}&aasUrl=${res.aasUrl}${query}`;
          });
        } else {
            window.location.href =
              process.env.PUBLIC_URL + '/' +
              'select-app?errorMessage=APPLICATION_NOT_FOUND';
        }
      });
    } else if (loggedIn.url !== '') {
      window.location.href = loggedIn.url + (loggedIn.url.includes('?') ? '&' : '?') + 'jwt=' + loggedIn.token;
    } else if (_task !== '' && loggedIn.value) {
      // user misses privilege
      window.location.href =
        process.env.PUBLIC_URL + '/select-app?missingPrivilege=true&errorMessage=MISSING_PRIVILEGES';

      yield putResolve(getLoginInfoAction());
    } else if (loggedIn.value) {
      yield putResolve(getLoginInfoAction());
    } else {
      yield putResolve(
        authenticationStateChanged({
          alias: undefined,
          authenticated: false,
          privileges: undefined,
          sso: loggedIn.sso,
          user: undefined,
        })
      );

      if (loggedIn.sso) {
        yield call(authRepository.ssoLogin, _task, _url);
      }
    }
}

function* watchGetLoginInfo() {
    yield takeEveryWithErrorHandling(ActionTypeNames.GetLoginInfo, getLoginInfo);
}

function* watchFetchAllData() {
    yield takeEveryWithErrorHandling(ActionTypeNames.FetchAllData, fetchAllData);
}

function* fetchAllData() {
    const res = yield all({
        applications: call(getAllApplications),
        groups: call(getAllGroups),
        servers: call(getAllServers),
        users: call(getAllUsers),
        workspaces: call(getAllWorkspaces),
    });
    yield all([
        putResolve(storeAllApplications(res.applications.items)),
        putResolve(storeAllGroups(res.groups.items)),
        putResolve(storeAllServers(res.servers.items)),
        putResolve(storeAllUsers(res.users.items)),
        putResolve(storeAllWorkspaces(res.workspaces.items)),
    ]);
}

function* getLoginInfo() {
    const profile: UserProfile = yield call(authRepository.getProfile);
    if (!profile.profile.is_old_user || profile.alias !== undefined) {
        const privileges = {
            assign: yield call(authRepository.getAssignPrivileges),
            own: yield call(authRepository.getUserPrivileges, profile.profile.id),
        };
        if (privileges.own.length > 0) {
            yield call(fetchAllData);
            yield putResolve(authenticationStateChanged({
                alias: profile.alias,
                authenticated: true,
                privileges,
                sso: profile.sso,
                organizationName: profile.organizationName,
                user: profile.profile
            }));
        } else {
            yield putResolve(authenticationStateChanged({
                alias: profile.alias,
                authenticated: true,
                privileges: { assign: [], own: [] },
                sso: profile.sso,
                organizationName: profile.organizationName,
                user: profile.profile
            }));
        }
    } else {
        yield putResolve(authenticationStateChanged({
            alias: profile.alias,
            authenticated: true,
            privileges: { assign: [], own: [] },
            sso: profile.sso,
            organizationName: profile.organizationName,
            user: profile.profile
        }));
    }
}

function* watchGetTheme() {
    yield takeEveryWithErrorHandling(ActionTypeNames.GetTheme, getTheme);
}

function* getTheme() {
    const theme: AASTheme = yield call(authRepository.getTheme);
    if (theme) {
        yield putResolve(setTheme(theme));
    }
}

function* watchGetAbout() {
    yield takeEveryWithErrorHandling(ActionTypeNames.GetAbout, getAbout);
}

function* getAbout() {
    const about: About = yield call(authRepository.getAbout);
    if (about) {
        yield putResolve(setAbout(about));
    }
}

function* watchGetSessions() {
    yield takeEveryWithErrorHandling(ActionTypeNames.GetSessions, getSessions);
}

function* getSessions() {
    const sessions: User[] = yield call(authRepository.getSessions);
    if (sessions) {
        yield putResolve(setSessions(sessions));
    }
}

function* watchGetSelfInfo() {
    yield takeEveryWithErrorHandling(ActionTypeNames.GetSelfInfo, getSelfInfo);
}

function* getSelfInfo() {
    const selfInfo: CheckSelfInfo = yield call(authRepository.getSelfInfo);
    if (selfInfo) {
        // Redirect a user to their only application if not an admin
        if (!selfInfo.admin && selfInfo.applications.length === 1) {
            // Only redirect if there is not an error
            const params: { [key: string]: string } = {};
            window.location.href.replace(/[?&]+([^=&]+)=([^&]*)/gi, (m, key, value) => {
                params[key] = value;
                return '';
            });
            if (params.errorApp === undefined && params.errorMessage === undefined) {
                yield putResolve(goToApplicationAction(selfInfo.applications[0].id));
            }
        }
        yield putResolve(setSelfInfo(selfInfo));
    }
}

function* watchLogin() {
    yield takeEveryWithErrorHandling(ActionTypeNames.Login, login);
}

function* login(action: LoginAction) {
    const res: { admin: boolean, session: string } =
        yield call(authRepository.login, action.email, action.password, action.remember_me,
            action.task, action.url, action.application, action.queryString);
    yield putResolve(getLoginInfoAction());
}

function* watchGoToApplication() {
    yield takeEveryWithErrorHandling(ActionTypeNames.GoToApplication, goToApplication);
}

function* goToApplication(action: GoToApplicationAction) {
    let res: ApplicationTokenResponse | undefined;
    try {
        res = yield call(authRepository.getApplicationToken, action.id);
    }
    catch (error) {
        // Maintenance mode redirect
        const errWrapper = JSON.parse(error.response);
        const err = errWrapper?.error || errWrapper;
        if (err?.statusCode === 401 && err?.message === "MAINTENANCE_MODE") 
        {
            const msg = err?.extraMessage ? encodeURIComponent(err.extraMessage) : "";
            yield call(() => {
                const baseUrl = process.env.PUBLIC_URL + "/select-app?errorMessage=MAINTENANCE_MODE";
                const url = msg !== ""
                    ? `${baseUrl}&extraMessage=${msg}`
                    : baseUrl;
                window.location.href = url;
            });
            return;
        }
    }
    if (res && res.token) {
        if (select(isLoggedInAs)) {
            yield call(authRepository.logoutAs);
        }
        if (res.cheops)
        {
            sessionStorage.setItem('accessToken', res.token);
            sessionStorage.setItem('core', `${res.anagoUrl}/core-api/api/v1`)
            sessionStorage.setItem('isConnected', 'false');

            const isSecure = window.location.protocol === 'https:';

            const coreApiUrl = window.location.hostname === 'localhost'
                ? 'http://localhost:8083/api/v1'
                : `${res.anagoUrl}/core-api/api/v1`;

            const hostname = window.location.hostname;
            const cookieDomain = hostname !== 'localhost' && hostname.split('.').length >= 3
                ? `.${hostname.split('.').slice(-2).join('.')}`
                : undefined;
            const cookieOpts = {
                secure: isSecure,
                sameSite: 'strict' as const,
                expires: 1,
                ...(cookieDomain ? { domain: cookieDomain } : {}),
            };

            Cookies.set('accessToken', res.token, cookieOpts);
            Cookies.set('core', coreApiUrl, cookieOpts);
            Cookies.set('isConnected', 'false', cookieOpts);
            Cookies.set('refreshToken', res.refreshToken, cookieOpts);
            Cookies.set('aasUrl', res.aasUrl, cookieOpts);
            Cookies.set('anagoUrl', res.anagoUrl, cookieOpts);
            
            yield call(() => { window.location.href = res!.platformUrl; });
        }
        else
        {
            yield call(() => window.location.replace(`${res!.anagoUrl}?accessToken=${res!.token}&aasUrl=${res!.aasUrl}`));
                        //  window.location.href = `${res.anagoUrl}?accessToken=${res.token}&aasUrl=${res.aasUrl}${queryString}`;
        }
    }
}

function* watchGoToZendesk() {
    yield takeEveryWithErrorHandling(ActionTypeNames.GoToZendesk, goToZendesk);
}

function* goToZendesk() {
    const res: { token: string, url: string } = yield call(authRepository.getZendeskToken);
    window.open(res.url + `?jwt=${res.token}&return_url=`);
}
function* watchAnonymousLogin() {
    yield takeEveryWithErrorHandling(ActionTypeNames.AnonymousLogin, anonymousLogin);
}

function* anonymousLogin(action: AnonymousLoginAction) {
    const res: ApplicationTokenResponse =
        yield call(authRepository.anonymousLogin, action.token);
    if (res.token) {
        yield call(() => window.location.replace(`${res.anagoUrl}?accessToken=${res.token}&aasUrl=${res.aasUrl}`));
    }
}

function* watchLoginAs() {
    yield takeEveryWithErrorHandling(ActionTypeNames.LoginAs, loginAs);
}

function* loginAs(action: LoginAsAction) {
    yield call(authRepository.loginAs, action.id);
    yield putResolve(checkLoginAction());
    if (action.refreshTable) {
        const selfInfo: CheckSelfInfo = yield call(authRepository.getSelfInfo);
        if (selfInfo.admin) {
            yield putResolve(goToPage('users', 0))
            yield putResolve(fetchTableUsers());
        }
    }
}

function* watchLogoutAs() {
    yield takeEveryWithErrorHandling(ActionTypeNames.LogoutAs, logoutAs);
}

function* logoutAs(action: LogoutAsAction) {
    yield call(authRepository.logoutAs);
    yield putResolve(checkLoginAction());
    if (action.checkSelf) {
        yield putResolve(getSelfInfoAction());
    }
    if (action.refreshTable) {
        switch (yield select(activePage)) {
            case 'users':
                yield putResolve(fetchTableUsers());
                break;
            case 'applications':
                yield putResolve(fetchTableApplications());
                break;
            case 'groups':
                yield putResolve(fetchTableGroups());
                break;
            case 'workspaces':
                yield putResolve(fetchTableWorkspaces());
                break;
            case 'servers':
                yield putResolve(fetchTableServers());
                break;
            default:
        }
    }
}

function* watchUpdateAccount() {
    yield takeEveryWithErrorHandling(ActionTypeNames.UpdateAccount, updateAccount);
}

function* updateAccount(action: UpdateAccountAction) {
    yield call(
        authRepository.updateAccount,
        action.email, action.confirmEmail,
        action.firstName, action.lastName);
    yield putResolve(dialogSuccess());
}

function* watchLogout() {
    yield takeEveryWithErrorHandling(ActionTypeNames.Logout, logout);
}

function* logout(action: LogoutAction) {
    if (yield select(ssoEnabled)) {
        yield call(authRepository.ssoLogout);
    } else {
        yield call(authRepository.logout);
    }
    yield putResolve(authenticationStateChanged({
        alias: undefined,
        authenticated: false,
        privileges: undefined,
        user: undefined
    }));
}

function* watchSendPasswordReset() {
    yield takeEveryWithErrorHandling(ActionTypeNames.SendPasswordReset, sendPasswordReset);
}

function* sendPasswordReset(action: SendPasswordResetAction) {
    yield call(authRepository.sendPasswordReset, action.email);
    yield putResolve(dialogSuccess());
}

function* watchResetPassword() {
    yield takeEveryWithErrorHandling(ActionTypeNames.ResetPassword, resetPassword);
}

function* resetPassword(action: ResetPasswordAction) {
    yield call(authRepository.resetPassword, action.id, action.token, action.new_password, action.confirm_new_password);
    yield putResolve(dialogSuccess());
    yield putResolve(checkLoginAction());
}

function* watchCheckResetPasswordToken() {
    yield takeEveryWithErrorHandling(
        ActionTypeNames.CheckResetPasswordToken,
        checkResetPasswordToken,
        invalidResetPasswordToken
    );
}

function* checkResetPasswordToken(action: CheckResetPasswordTokenAction) {
    yield call(authRepository.checkResetPasswordToken, action.id, action.token);
    yield putResolve(setInvalidToken(false));
}

function* invalidResetPasswordToken() {
    yield putResolve(setInvalidToken(true));
}

function* watchConfirmActiveDirectory() {
    yield takeEveryWithErrorHandling(
        ActionTypeNames.ConfirmActiveDirectory,
        confirmActiveDirectory,
        invalidConfirmActiveDirectoryToken,
    );
}

function* confirmActiveDirectory(action: ConfirmActiveDirectoryAction) {
    yield call(authRepository.confirmActiveDirectory, action.id, action.token);
    yield putResolve(setInvalidToken(false));
    yield putResolve(dialogSuccess());
}

function* invalidConfirmActiveDirectoryToken() {
    yield putResolve(setInvalidToken(true));
}

function* watchVerifyEmail() {
    yield takeEveryWithErrorHandling(
        ActionTypeNames.VerifyEmail,
        verifyEmail,
        invalidConfirmActiveDirectoryToken,
    );
}

function* verifyEmail(action: VerifyEmailAction) {
    yield call(authRepository.verifyEmail, action.id, action.token);
    yield putResolve(setInvalidToken(false));
    yield putResolve(dialogSuccess());
}

export default [watchLogin, watchGetLoginInfo, watchCheckLogin, watchLogout, watchSendPasswordReset,
    watchResetPassword, watchCheckResetPasswordToken, watchGetTheme, watchLoginAs, watchLogoutAs,
    watchFetchAllData, watchGetAbout, watchGetSessions, watchGetSelfInfo, watchGoToApplication,
    watchUpdateAccount, watchAnonymousLogin, watchGoToZendesk, watchConfirmActiveDirectory, watchVerifyEmail];
